/**
 * The choices /audio-converter and /audio-joiner offer (SEO_BRIEF_11 section 2.1), and the order of
 * the joiner's files: pure, with no mediabunny and no encoder, so the page's bundle imports them
 * without pulling in the engine (audio-convert.ts and audio-join.ts re-export them).
 */

/** The MP3 bit rates the page offers, in kbit/s. All three are legal at every MPEG-1 rate. */
export const MP3_BITRATES = [128, 192, 320] as const;
export type Mp3Kbps = (typeof MP3_BITRATES)[number];
/** The page's first choice: /mp4-to-mp3's rate. */
export const DEFAULT_KBPS: Mp3Kbps = 192;

/** What the converter writes: an MP3 at one of the three rates, or a WAV. Nothing else. */
export type ConvertTarget =
  | { readonly kind: 'mp3'; readonly kbps: Mp3Kbps }
  | { readonly kind: 'wav' };

/** True for one of the three rates the page offers. */
export function isMp3Kbps(value: unknown): value is Mp3Kbps {
  return (MP3_BITRATES as readonly unknown[]).includes(value);
}

/** A choice as the page's radio values spell it (`mp3-192`, `wav`), or null for anything else. */
export function parseTarget(value: string): ConvertTarget | null {
  if (value === 'wav') return { kind: 'wav' };
  const m = /^mp3-(\d+)$/.exec(value);
  const kbps = m ? Number(m[1]) : Number.NaN;
  return isMp3Kbps(kbps) ? { kind: 'mp3', kbps } : null;
}

/** The radio value for a target: the inverse of `parseTarget`. */
export function targetValue(target: ConvertTarget): string {
  return target.kind === 'wav' ? 'wav' : `mp3-${target.kbps}`;
}

/** The gaps the page offers between files, in seconds. */
export const JOIN_GAPS = [0, 1, 2] as const;
export type JoinGap = (typeof JOIN_GAPS)[number];
/** "A short crossfade": each file fades into the next over this many seconds. */
export const CROSSFADE_S = 2;

/** What goes between two files. */
export type JoinSpacing =
  | { readonly kind: 'gap'; readonly seconds: JoinGap }
  | { readonly kind: 'crossfade' };

/** A choice as the page's radio values spell it (`gap-0`, `gap-2`, `crossfade`), or null. */
export function parseSpacing(value: string): JoinSpacing | null {
  if (value === 'crossfade') return { kind: 'crossfade' };
  const m = /^gap-(\d)$/.exec(value);
  const seconds = m ? Number(m[1]) : Number.NaN;
  return (JOIN_GAPS as readonly number[]).includes(seconds)
    ? { kind: 'gap', seconds: seconds as JoinGap }
    : null;
}

/** `list` with the item at `from` moved to `to`. A copy; an index out of range changes nothing. */
export function moveItem<T>(list: readonly T[], from: number, to: number): T[] {
  const out = [...list];
  if (from < 0 || from >= out.length || to < 0 || to >= out.length || from === to) return out;
  const [item] = out.splice(from, 1);
  out.splice(to, 0, item as T);
  return out;
}
